import {
  CONFIGURATION_SCHEMAS_BY_LAYER_TYPE,
  GENERIC_CONFIGURATION_SCHEMA,
} from "@/v2-backoffice-client/layer/update/schema/layer.update.schema.configuration"
import {
  FILTERS_SCHEMA,
  INTERACTIONS_SCHEMA,
  EXPORT_SCHEMA,
  TABLE_SCHEMA,
} from "@/v2-backoffice-client/layer/update/schema/layer.update.schema.fields"
import { getCatalogEditorModelPath } from "@/v2-backoffice-client/feature/configuration/feature.configuration.constants"
import type { JsonSchema } from "@/v2-backoffice-client/feature/configuration/feature.configuration.types"

export type LayerEditorField =
  | "Configuration"
  | "Filters"
  | "Interactions"
  | "Export"
  | "Table"

function getConfigurationModelName(layerTypeCode: string) {
  const isKnownType = layerTypeCode in CONFIGURATION_SCHEMAS_BY_LAYER_TYPE
  return `configuration.${isKnownType ? layerTypeCode : "generic"}.json`
}

export function getLayerEditorModelPath(
  field: LayerEditorField,
  layerTypeCode: string,
) {
  return getCatalogEditorModelPath(
    field === "Configuration"
      ? getConfigurationModelName(layerTypeCode)
      : `${field.toLowerCase()}.json`,
  )
}

const FIELD_SCHEMAS: Record<
  Exclude<LayerEditorField, "Configuration">,
  JsonSchema
> = {
  Filters: FILTERS_SCHEMA,
  Interactions: INTERACTIONS_SCHEMA,
  Export: EXPORT_SCHEMA,
  Table: TABLE_SCHEMA,
}

/** The curated schema backing a given editor tab (drives the inspector). */
export function getLayerEditorSchema(
  field: LayerEditorField,
  layerTypeCode: string,
): JsonSchema {
  if (field === "Configuration") {
    return (
      CONFIGURATION_SCHEMAS_BY_LAYER_TYPE[layerTypeCode] ??
      GENERIC_CONFIGURATION_SCHEMA
    )
  }
  return FIELD_SCHEMAS[field]
}

/**
 * The layer catalog's contribution to the shared Monaco registry, keyed by
 * model name (see feature.configuration.schema).
 */
export const LAYER_EDITOR_SCHEMAS: Record<string, JsonSchema> = {
  ...Object.fromEntries(
    Object.entries(CONFIGURATION_SCHEMAS_BY_LAYER_TYPE).map(
      ([layerTypeCode, schema]) => [
        `configuration.${layerTypeCode}.json`,
        schema,
      ],
    ),
  ),
  "configuration.generic.json": GENERIC_CONFIGURATION_SCHEMA,
  "filters.json": FILTERS_SCHEMA,
  "interactions.json": INTERACTIONS_SCHEMA,
  "export.json": EXPORT_SCHEMA,
  "table.json": TABLE_SCHEMA,
}
